/**
 * The Hour Optimizer in the Flow Optimizer's window (user, 12/10/2026): View
 * tabs Formula (what scores an hour) · Rules (Not enough data and the
 * rest) · Config (extra drops, space between), and the campaign's day hour
 * by hour on the right, live with the settings being edited. The section's
 * on/off and its source (own value or the master config's) sit in the header.
 * One read (`useHourExtras`) feeds the panel, the rule cards' notes and the
 * Config tab's room check: everything shows the same extra drops.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ListChecks, Settings2, Sigma } from 'lucide-react';
import { containerWindow, effectiveHourFormula } from '@trafficpulse/shared';

import { Switch } from '@/components/ui';

import { InheritTag } from '../../master/InheritTag';
import { DialogSubject } from '../../optimizer-dialog/DialogSubject';
import { OptimizerBody } from '../../optimizer-dialog/OptimizerDialog';
import { RulesCard } from '../../rules/RulesCard';
import type { ContainerEditor } from '../../state/useContainerEditor';
import type { CountryWindow } from '../hour/hour-chart-math';
import { useHourExtras } from '../hour/extras/use-hour-extras';
import { HourFormulaCard, lookbackFor } from '../hour/formula';
import { HourProfilePanel } from '../hour/HourProfilePanel';
import { HourConfigRows } from './HourConfigRows';

const FORMULA_HINT =
  'Like the Flow Optimizer: each date range has its own formula (sliders or blocks); with several ranges their shares add up to 100%. Rates (CR, ROI, EPV…) compare hours fairly; counts mostly re-elect the hours already used most.';

interface Props {
  editor: ContainerEditor;
  countryKey: string;
  startMin: number | null;
  /** The end time on the country's clock — the stored one is Danish (15/10/2026). */
  endMin: number | null;
  slotMinutes: number[];
  window: CountryWindow;
  onClose: () => void;
}

export const HourOptimizerContent: FC<Props> = ({ editor, countryKey, startMin, endMin, slotMinutes, window: countryWin, onClose }) => {
  const { draft, setHourOptimizer } = editor;
  const window = containerWindow(countryWin, draft.sendOutsideWindow); // 14/10/2026 — none when the container sends outside it
  const config = draft.hourOptimizer;
  const formula = effectiveHourFormula(config);
  const read = useHourExtras({ countryKey, campaignWiserId: draft.campaignWiserId, config, startMin, endAtMin: endMin, slotMinutes, window });
  return (
    <OptimizerBody
      title="Hour Optimizer"
      subtitle={<DialogSubject editor={editor} />}
      headerAside={
        <>
          <InheritTag section="hourOptimizer" masterValue={(m) => (m.hourOptimizerOn ? 'on' : 'off')} />
          <Switch checked={config.enabled} onCheckedChange={(enabled) => setHourOptimizer({ enabled })} aria-label="Hour Optimizer enabled" />
        </>
      }
      tabs={[
        { value: 'formula', label: 'Formula', icon: Sigma, content: <HourFormulaCard formula={formula} hint={FORMULA_HINT} onChange={(next) => setHourOptimizer({ formula: next, lookbackPreset: lookbackFor(next) })} /> },
        { value: 'rules', label: 'Rules', icon: ListChecks, content: <RulesCard surface="hours" rules={config.rules} onChange={(rules) => setHourOptimizer({ rules })} firstRange={formula.groups[0]} notes={read.notes} /> },
        { value: 'config', label: 'Config', icon: Settings2, content: <HourConfigRows editor={editor} span={read.span} /> },
      ]}
      panel={<HourProfilePanel read={read} countryKey={countryKey} campaignWiserId={draft.campaignWiserId} config={config} startMin={startMin} endMin={endMin} slotMinutes={slotMinutes} window={window} />}
      panelWidthClass="lg:w-[46rem]"
      onClose={onClose}
    />
  );
};
